import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { ColorSchemes } from 'src/styles/color-scheme.type';
import { PendingTaskCounter } from '../../classes/pending-task-counter';
import { FileInStorageHlp } from '../../helpers/file.helper';
import { EditedImageVM } from '../../models/editem-image.view-model';
import { FileData, FileInStorageVM, FileSummaryData } from '../../models/file.data';
import { DeleteFileService } from '../../services/delete-file.service';
import { DialogService } from '../../services/dialog.service';
import { FireStorageService } from '../../services/storage.service';
import { CropImageComponent, CropperSettings } from '../crop-image/crop-image.component';

@Component({
  selector: 'app-add-picture-field[uploadFilePath]',
  templateUrl: './add-picture-field.component.html',
  styleUrls: ['./add-picture-field.component.scss']
})
export class AddPictureFieldComponent implements OnChanges, OnDestroy {
  @Input() images: FileInStorageVM[] = [];
  @Input() uploadFilePath!: string;
  @Input() maxImages: number = 10;
  /** size limit of a single file in bytes, 0 means no limit */
  @Input() fileSizeLimit: number = 0;
  @Input() mimeTypes: string[] = [];
  @Input() disabled = false;
  @Input() withCropper: boolean = false;
  @Input() cropperSettings: CropperSettings = { aspectRatio: 1, resizeToWidth: 100, roundCropper: false };
  /** @deprecated */
  @Input() color: 'primary' | 'offers' = 'primary';
  @Input() colors: ColorSchemes = ColorSchemes.PRIMARY;

  @Output() onSumSizeChange = new EventEmitter<number>();

  public imagesToUpload: FileData[] = [];
  public imagesWithSize: FileInStorageVM[] = [];

  public fileSizeSum: number = 0;
  public maxCountExceeded = false;

  private pendingImages = new PendingTaskCounter();
  private currentImagesLoading = false;
  private imagesLoadVersion = 0;
  private lastInputImages: FileInStorageVM[] | null = null;
  private operationVersion = 0;

  public get imagesCount(): number {
    const currentImagesCount = this.currentImagesLoading ? this.images.length : this.imagesWithSize.length;
    return currentImagesCount + this.imagesToUpload.length + this.pendingImages.count;
  }

  public get hasPendingImages(): boolean {
    return this.pendingImages.count > 0;
  }

  private get availableImagesCount(): number {
    return Math.max(this.maxImages - this.imagesCount, 0);
  }

  ColorSchemes = ColorSchemes;
  constructor(
    private fs: FireStorageService,
    private deleteFileService: DeleteFileService,
    private dialogService: DialogService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.images) return;

    const images = this.images ?? [];
    if (this.areSameImages(images, this.lastInputImages)) return;

    this.images = images;
    this.lastInputImages = this.copyImages(images);
    this.loadStoredImages(images, this.imagesWithSize);
  }

  ngOnDestroy(): void {
    this.imagesLoadVersion++;
    this.invalidatePendingOperations();
    this.deleteFileService.clearFilesToDelete();
  }

  /** Discards local edits and loads the supplied stored images as the new field state. */
  public reset(images: FileInStorageVM[] = this.images): void {
    const nextImages = images ?? [];
    this.invalidatePendingOperations();

    this.images = nextImages;
    this.lastInputImages = this.copyImages(nextImages);
    this.imagesToUpload = [];
    this.imagesWithSize = [];
    this.maxCountExceeded = false;
    this.deleteFileService.clearFilesToDelete();
    this.loadStoredImages(nextImages, []);
  }

  public addFile(file: File): void {
    if (!file || this.disabled) return;
    if (!this.availableImagesCount) {
      this.maxCountExceeded = true;
      return;
    }

    this.maxCountExceeded = false;
    const operationVersion = this.operationVersion;
    const completePendingImage = this.pendingImages.start();
    const completeIfCurrent = (): void => {
      if (operationVersion === this.operationVersion) completePendingImage();
    };
    if (this.withCropper) {
      const avatarCropper = this.dialogService.openSimpleDialog(
        CropImageComponent,
        { image: file, ...this.cropperSettings },
        { width: 'auto', height: 'auto', autoFocus: false }
      );
      avatarCropper.afterClosed().subscribe((result) => {
        if (operationVersion !== this.operationVersion) return;

        if (!result?.croppedFile) {
          completeIfCurrent();
          return;
        }
        this.handleFile(result.croppedFile, completeIfCurrent, operationVersion);
      });
    } else {
      this.handleFile(file, completeIfCurrent, operationVersion);
    }
  }

  public addFiles(files: File[]): void {
    const availableImagesCount = this.availableImagesCount;
    const maxCountExceeded = files.length > availableImagesCount;
    files.slice(0, availableImagesCount).forEach((file) => this.addFile(file));
    this.maxCountExceeded = maxCountExceeded;
  }

  private handleFile(file: File, completePendingImage: () => void, operationVersion: number): void {
    const reader = new FileReader();
    reader.onload = () => {
      if (operationVersion !== this.operationVersion) return;

      this.imagesToUpload.push({ file, rawData: reader.result as string, size: file.size });
      completePendingImage();
      this.updateFileSizeSum();
    };
    reader.onerror = completePendingImage;
    reader.onabort = completePendingImage;
    try {
      reader.readAsDataURL(file);
    } catch (err) {
      completePendingImage();
      throw err;
    }
  }

  public removeFile(index: number, uploaded: boolean): void {
    if (this.disabled) return;

    if (uploaded) {
      this.imagesToUpload = this.imagesToUpload.filter((_, i) => i !== index);
      this.maxCountExceeded = false;
      this.updateFileSizeSum();
    } else {
      const operationVersion = this.operationVersion;
      const imageToDelete = { ...this.imagesWithSize[index] };
      this.deleteFileService.confirmDelete$(imageToDelete.name, this.colors).subscribe((result) => {
        if (operationVersion !== this.operationVersion) return;

        if (result) {
          this.deleteFileService.addFileToDelete(imageToDelete.storagePath);

          // HACK more universal solution in future
          const editedImage = imageToDelete as EditedImageVM;
          if (editedImage.originalPath) this.deleteFileService.addFileToDelete(editedImage.originalPath);

          this.imagesWithSize = this.imagesWithSize.filter((_, i) => i !== index);
          this.maxCountExceeded = false;
          this.updateFileSizeSum();
        }
      });
    }
  }

  /**
   * NOT USED IN COMPONENT, BUT IN PARENTS
   * ! Should be always called after [dbObject].modify()
   */
  public async handleDeleted(): Promise<void> {
    await this.deleteFileService.deleteFiles();
  }

  /**
   * ! USED IN PARENT COMPONENT
   * @return List of current and to upload attachments
   */
  public getSummaryData(): FileSummaryData {
    return {
      current: this.currentImagesLoading ? this.images : this.imagesWithSize,
      toUpload: this.imagesToUpload
    };
  }

  public whenIdle(): Promise<void> {
    return this.pendingImages.whenIdle();
  }

  /**
   * NOT USED IN COMPONENT, BUT IN PARENTS
   * @return Images added and existing (WITHOUT deleted)
   */
  public async onSubmit(): Promise<FileInStorageVM[]> {
    await this.whenIdle();

    return [
      ...this.getSummaryData().current,
      ...(await this.fs.uploadFilesToStorage(
        this.imagesToUpload.map((i) => i.file!),
        this.uploadFilePath
      ))
    ];
  }

  private updateFileSizeSum(): void {
    const upl = this.imagesToUpload.reduce((sum, file) => sum + file.size, 0);
    const img = this.imagesWithSize.reduce((sum, file) => sum + (file.size || 0), 0);
    this.fileSizeSum = upl + img;
    this.onSumSizeChange.emit(this.fileSizeSum);
  }

  private loadStoredImages(images: FileInStorageVM[], currentImages: FileInStorageVM[]): void {
    const loadVersion = ++this.imagesLoadVersion;
    this.currentImagesLoading = images.length > 0;
    if (!this.currentImagesLoading) {
      this.imagesWithSize = currentImages;
      this.updateFileSizeSum();
      return;
    }

    void FileInStorageHlp.copyAndAppendFiles(this.copyImages(images), currentImages, this.fs).then((copy) => {
      if (loadVersion !== this.imagesLoadVersion) return;

      this.imagesWithSize = copy;
      this.currentImagesLoading = false;
      this.updateFileSizeSum();
    });
  }

  private areSameImages(images: FileInStorageVM[], previous: FileInStorageVM[] | null): boolean {
    return (
      previous !== null &&
      images.length === previous.length &&
      images.every((image, index) => FileInStorageHlp.areEqual(image, previous[index]))
    );
  }

  private copyImages(images: FileInStorageVM[]): FileInStorageVM[] {
    return images.map((image) => ({ ...image }));
  }

  private invalidatePendingOperations(): void {
    this.operationVersion++;
    this.pendingImages.clear();
    this.pendingImages = new PendingTaskCounter();
  }
}
